import { defaultFiltersState, FiltersState } from '../hooks/useFilters';
import { normalizeSortByForSource } from './sourceSort';
import { getRegisteredSourceModules, getSourceModule } from './sourceModules';

type FilterKey = keyof FiltersState;

const ESTAFF_SOURCES = new Set(['estaff', 'skillaz']);
const LEGACY_BUCKET_SOURCES = new Set(['hh', 'estaff', 'skillaz']);

const DEFAULT_TRANSFER_KEYS: FilterKey[] = [
  'positionName',
  'cities',
  'cityIds',
  'salaryFrom',
  'salaryTo',
  'salaryCurrency',
  'ageFrom',
  'ageTo',
  'includeNoAge',
  'gender',
  'skillsAny',
  'skillsAll',
  'skillGroups',
];

const NON_ESTAFF_KEYS: FilterKey[] = [
  'expectedPositionName',
  'currentCompany',
  'previousCompany',
  'currentPositionOnly',
  'updatedAfter',
  'updatedBefore',
  'createdAfter',
  'createdBefore',
  'onlyMyCandidates',
];

const OPTIONAL_FILTER_DEFAULTS: Partial<Record<FilterKey, unknown>> = {
  currentCompany: '',
  previousCompany: '',
  currentPositionOnly: false,
  updatedAfter: '',
  updatedBefore: '',
  createdAfter: '',
  createdBefore: '',
};

const ESTAFF_ALLOWED_FILTER_KEYS: FilterKey[] = [
  'source',
  'limit',
  'page',
  'pageSize',
  'excludeUpdatedPeriod',
  'salaryFrom',
  'salaryTo',
  'salaryCurrency',
  'incomeSpecified',
  'hasSalary',
  'cities',
  'cityIds',
  'ageFrom',
  'ageTo',
  'includeNoAge',
  'gender',
  'stopCompanies',
  'donorCompanies',
  'companyFilterMode',
  'positionName',
  'expectedPositionName',
  'skillsAny',
  'skillsAll',
  'skillGroups',
  'searchMode',
  'strictSearch',
  'scoringEnabled',
  'currentPositionOnly',
  'updatedAfter',
  'updatedBefore',
  'createdAfter',
  'createdBefore',
  'onlyMyCandidates',
];

const isEstaffSource = (source: string) => ESTAFF_SOURCES.has(source || defaultFiltersState.source);

const getKnownFilterKeys = (): FilterKey[] => Array.from(new Set([
  ...Object.keys(defaultFiltersState),
  ...Object.keys(OPTIONAL_FILTER_DEFAULTS),
] as FilterKey[]));

const ESTAFF_RESET_KEYS: FilterKey[] = getKnownFilterKeys()
  .filter((key) => !ESTAFF_ALLOWED_FILTER_KEYS.includes(key));

const getDefaultFilterValue = (key: FilterKey) => {
  if (Object.prototype.hasOwnProperty.call(defaultFiltersState, key)) {
    return (defaultFiltersState as any)[key];
  }

  return (OPTIONAL_FILTER_DEFAULTS as any)[key];
};

const getSerializableDefaultValue = (defaults: FiltersState, key: FilterKey) => {
  if (Object.prototype.hasOwnProperty.call(defaults, key)) {
    return (defaults as any)[key];
  }

  return getDefaultFilterValue(key);
};

const resetKeysToDefault = (target: FiltersState, keys: FilterKey[]) => {
  keys.forEach((key) => {
    (target as any)[key] = getDefaultFilterValue(key);
  });
};

export const getSourceFilterBucketId = (source: string): string => (
  getSourceModule(source)?.filters?.bucketId || 'legacy'
);

const isLegacyBucketSource = (source: string) => (
  LEGACY_BUCKET_SOURCES.has(source || defaultFiltersState.source)
);

const shouldOverlaySharedFilters = (fromSource: string, toSource: string) => (
  isLegacyBucketSource(fromSource)
  && isLegacyBucketSource(toSource)
  && getSourceFilterBucketId(fromSource) === getSourceFilterBucketId(toSource)
);

export const getTransferableFilters = (
  fromSource: string,
  toSource: string,
  currentFilters: Partial<FiltersState>,
): Partial<FiltersState> => {
  const targetTransfer = getSourceModule(toSource)?.filters?.getTransferableFilters;
  if (targetTransfer) {
    return targetTransfer({ fromSource, toSource, currentFilters });
  }

  const sourceTransfer = getSourceModule(fromSource)?.filters?.getTransferableFilters;
  if (sourceTransfer) {
    return sourceTransfer({ fromSource, toSource, currentFilters });
  }

  return DEFAULT_TRANSFER_KEYS.reduce<Partial<FiltersState>>((acc, key) => {
    const value = (currentFilters as any)[key];
    if (value !== undefined) {
      (acc as any)[key] = value;
    }
    return acc;
  }, {});
};

export const sanitizeFiltersForSource = (
  source: string,
  filters: Partial<FiltersState> = {},
): FiltersState => {
  const targetSource = source || filters.source || defaultFiltersState.source;
  const sourceSanitizer = getSourceModule(targetSource)?.filters?.sanitize;
  if (sourceSanitizer) {
    return sourceSanitizer(filters);
  }

  const next = {
    ...defaultFiltersState,
    ...filters,
    source: targetSource,
  } as FiltersState;

  getRegisteredSourceModules().forEach((sourceModule) => {
    if (sourceModule.id !== targetSource) {
      sourceModule.filters?.clearOwnedFields?.(next);
    }
  });

  if (isEstaffSource(targetSource)) {
    resetKeysToDefault(next, ESTAFF_RESET_KEYS);
  } else {
    resetKeysToDefault(next, NON_ESTAFF_KEYS);
  }

  const normalizedSortBy = normalizeSortByForSource(targetSource, next.sortBy);
  if (normalizedSortBy) {
    next.sortBy = normalizedSortBy;
  }

  return next;
};

export const getDefaultFiltersForSource = (source: string): FiltersState => {
  const moduleDefaults = getSourceModule(source)?.filters?.getDefaultFilters;
  if (moduleDefaults) {
    return moduleDefaults();
  }

  return sanitizeFiltersForSource(source, {
    ...defaultFiltersState,
    source,
    strictSearch: source === defaultFiltersState.source
      ? defaultFiltersState.strictSearch
      : false,
  });
};

const areEqualValues = (left: unknown, right: unknown): boolean => (
  JSON.stringify(left) === JSON.stringify(right)
);

const isEmptySerializableValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const getSerializableKeysForSource = (source: string): FilterKey[] => {
  if (isEstaffSource(source)) {
    return ESTAFF_ALLOWED_FILTER_KEYS;
  }

  return Object.keys(defaultFiltersState) as FilterKey[];
};

export const getSerializableFiltersForSource = (
  source: string,
  filters: Partial<FiltersState> = {},
): Partial<FiltersState> => {
  const moduleSerializer = getSourceModule(source)?.filters?.getSerializableFilters;
  if (moduleSerializer) {
    return moduleSerializer(filters);
  }

  const sanitized = sanitizeFiltersForSource(source, filters);
  const defaults = getDefaultFiltersForSource(source);
  const serializable: Partial<FiltersState> = {};

  getSerializableKeysForSource(source).forEach((key) => {
    const value = (sanitized as any)[key];
    if (key === 'source') {
      (serializable as any)[key] = sanitized.source;
      return;
    }
    if (isEmptySerializableValue(value)) return;
    if (areEqualValues(value, getSerializableDefaultValue(defaults, key))) return;
    (serializable as any)[key] = value;
  });

  return serializable;
};

interface BuildFiltersForSourceSwitchParams {
  targetSource: string;
  currentFilters: FiltersState;
  savedTargetFilters?: Partial<FiltersState>;
}

export const buildFiltersForSourceSwitch = ({
  targetSource,
  currentFilters,
  savedTargetFilters,
}: BuildFiltersForSourceSwitchParams): FiltersState => {
  const currentSource = currentFilters.source || defaultFiltersState.source;
  const targetBuilder = getSourceModule(targetSource)?.filters?.buildForSourceSwitch;
  if (targetBuilder) {
    return targetBuilder({ targetSource, currentFilters, savedTargetFilters });
  }

  if (savedTargetFilters) {
    if (shouldOverlaySharedFilters(currentSource, targetSource)) {
      return sanitizeFiltersForSource(targetSource, {
        ...savedTargetFilters,
        ...getTransferableFilters(currentSource, targetSource, currentFilters),
        source: targetSource,
        page: 1,
      });
    }

    return sanitizeFiltersForSource(targetSource, {
      ...savedTargetFilters,
      source: targetSource,
      page: 1,
    });
  }

  return sanitizeFiltersForSource(targetSource, {
    ...getDefaultFiltersForSource(targetSource),
    ...getTransferableFilters(currentSource, targetSource, currentFilters),
    source: targetSource,
    page: 1,
    strictSearch: false,
  });
};

export interface SourceFilterBuckets {
  version: 1;
  updatedAt: number;
  bySource: Record<string, Partial<FiltersState>>;
}

const createEmptySourceFilterBuckets = (): SourceFilterBuckets => ({
  version: 1,
  updatedAt: Date.now(),
  bySource: {},
});

export const getSourceBucketsStorageKey = (searchStateKey: string): string => (
  `${searchStateKey}:sourceBuckets`
);

export const readSourceFilterBuckets = (
  storage: Storage | undefined | null,
  storageKey: string,
): SourceFilterBuckets => {
  if (!storage) return createEmptySourceFilterBuckets();

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return createEmptySourceFilterBuckets();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return createEmptySourceFilterBuckets();

    return {
      version: 1,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      bySource: parsed.bySource && typeof parsed.bySource === 'object' ? parsed.bySource : {},
    };
  } catch {
    return createEmptySourceFilterBuckets();
  }
};

export const saveFiltersForSource = (
  storage: Storage | undefined | null,
  storageKey: string,
  filters: Partial<FiltersState>,
): void => {
  if (!storage) return;

  const source = filters.source || defaultFiltersState.source;
  const buckets = readSourceFilterBuckets(storage, storageKey);
  buckets.bySource[source] = getSerializableFiltersForSource(source, filters);
  buckets.updatedAt = Date.now();

  try {
    storage.setItem(storageKey, JSON.stringify(buckets));
  } catch {
    // localStorage/sessionStorage могут быть недоступны или переполнены.
    // Поиск продолжит работать в памяти, поэтому bucket — best-effort.
  }
};

export const getSavedFiltersForSource = (
  storage: Storage | undefined | null,
  storageKey: string,
  source: string,
): FiltersState | undefined => {
  const buckets = readSourceFilterBuckets(storage, storageKey);
  const saved = buckets.bySource[source];
  if (!saved) return undefined;

  return sanitizeFiltersForSource(source, {
    ...saved,
    source,
  });
};
